import React, { useEffect, useMemo, useState } from "react";

import { cn } from "@/lib/utils";

type VisualizationVariant = "standCount" | "imageMeasurement";

interface PitchAngleVisualizationProps {
  minAngle: number;
  maxAngle: number;
  minDistanceCm: number;
  maxDistanceCm: number;
  variant?: VisualizationVariant;
  boxSizeCm?: number;
  hasMinAngle?: boolean;
  hasMaxAngle?: boolean;
  className?: string;
  paused?: boolean;
}

const DISTANCE_LIMITS = { min: 30, max: 500 };
const BOX_SIZE_LIMITS = { min: 5, max: 200 };
const GROUND_LINE = { x1: 118, x2: 438, y: 208 };
const GROUND_ANCHOR = { x: 302, y: GROUND_LINE.y };
const PLANT_CENTER = { x: GROUND_ANCHOR.x, y: GROUND_LINE.y + 6 };
const STAND_COUNT_PHONE_DISTANCE_RANGE = { near: 96, far: 156 };
const IMAGE_PHONE_Y_RANGE = { near: 114, far: 86 };
const IMAGE_LINE_LENGTH_RANGE = { near: 68, far: 108 };
const BOX_LINE_RANGE = { min: 40, max: 132 };
const GUIDE_PADDING = 16;
const LABEL_PADDING = 18;
const BOX_EDGE_COLOR = "hsl(35 92% 52%)";
const LEGEND_ORIGIN = { x: 24, y: 56 };

const toRadians = (angle: number) => (angle * Math.PI) / 180;

const clampValue = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const interpolateRange = (value: number, inputMin: number, inputMax: number, outputMin: number, outputMax: number) => {
  if (inputMax === inputMin) {
    return outputMin;
  }

  const ratio = (value - inputMin) / (inputMax - inputMin);
  return outputMin + ratio * (outputMax - outputMin);
};

const getAngleVector = (angle: number) => {
  const radians = toRadians(angle);

  return {
    x: Math.sin(radians),
    y: Math.cos(radians),
  };
};

const getPointAlongAngle = (origin: { x: number; y: number }, angle: number, distance: number) => {
  const vector = getAngleVector(angle);

  return {
    x: origin.x + vector.x * distance,
    y: origin.y + vector.y * distance,
  };
};

const getGroundIntersection = (origin: { x: number; y: number }, angle: number, groundY: number) => {
  const vector = getAngleVector(angle);
  const distance = Math.max((groundY - origin.y) / Math.max(vector.y, 0.12), 0);

  return {
    distance,
    point: {
      x: origin.x + vector.x * distance,
      y: groundY,
    },
  };
};

const resolveAngleState = ({
  minAngle,
  maxAngle,
  hasMinAngle,
  hasMaxAngle,
}: {
  minAngle: number;
  maxAngle: number;
  hasMinAngle: boolean;
  hasMaxAngle: boolean;
}) => {
  if (hasMinAngle && hasMaxAngle) {
    return {
      resolvedMinAngle: minAngle,
      resolvedMaxAngle: maxAngle,
      centerAngle: (minAngle + maxAngle) / 2,
      animationAmplitude: Math.max((maxAngle - minAngle) / 2, 0),
    };
  }

  if (hasMinAngle) {
    return {
      resolvedMinAngle: minAngle,
      resolvedMaxAngle: minAngle,
      centerAngle: minAngle,
      animationAmplitude: 0,
    };
  }

  if (hasMaxAngle) {
    return {
      resolvedMinAngle: maxAngle,
      resolvedMaxAngle: maxAngle,
      centerAngle: maxAngle,
      animationAmplitude: 0,
    };
  }

  return {
    resolvedMinAngle: 0,
    resolvedMaxAngle: 0,
    centerAngle: 0,
    animationAmplitude: 0,
  };
};

const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);

    updatePreference();
    mediaQuery.addEventListener?.("change", updatePreference);

    return () => {
      mediaQuery.removeEventListener?.("change", updatePreference);
    };
  }, []);

  return prefersReducedMotion;
};

const PitchAngleVisualization: React.FC<PitchAngleVisualizationProps> = ({
  minAngle,
  maxAngle,
  minDistanceCm,
  maxDistanceCm,
  variant = "standCount",
  boxSizeCm,
  hasMinAngle = false,
  hasMaxAngle = false,
  className,
  paused = false,
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const angleState = useMemo(
    () =>
      resolveAngleState({
        minAngle,
        maxAngle,
        hasMinAngle,
        hasMaxAngle,
      }),
    [hasMaxAngle, hasMinAngle, maxAngle, minAngle]
  );
  const [displayAngle, setDisplayAngle] = useState(angleState.centerAngle);

  useEffect(() => {
    if (paused || prefersReducedMotion || angleState.animationAmplitude === 0) {
      setDisplayAngle(angleState.centerAngle);
      return;
    }

    let animationFrame = 0;
    let startTime = 0;

    const animate = (timestamp: number) => {
      if (startTime === 0) {
        startTime = timestamp;
      }

      const elapsed = (timestamp - startTime) / 1000;
      const nextAngle = angleState.centerAngle + angleState.animationAmplitude * Math.sin(elapsed * 2.1);
      setDisplayAngle(nextAngle);
      animationFrame = window.requestAnimationFrame(animate);
    };

    animationFrame = window.requestAnimationFrame(animate);

    return () => {
      window.cancelAnimationFrame(animationFrame);
    };
  }, [angleState.animationAmplitude, angleState.centerAngle, paused, prefersReducedMotion]);

  const preview = useMemo(() => {
    const clampedMaxDistance = clampValue(maxDistanceCm, DISTANCE_LIMITS.min, DISTANCE_LIMITS.max);
    const clampedMinDistance = clampValue(
      minDistanceCm,
      DISTANCE_LIMITS.min,
      Math.max(DISTANCE_LIMITS.min, clampedMaxDistance - 1)
    );
    const goodDistanceRatio = (clampedMaxDistance - clampedMinDistance) / clampedMaxDistance;
    const showBox = variant === "standCount" && typeof boxSizeCm === "number";

    let phoneCenter: { x: number; y: number };
    let distanceLineEnd: { x: number; y: number };
    let validDistanceEnd: { x: number; y: number };
    let minGuideEnd: { x: number; y: number } | null = null;
    let maxGuideEnd: { x: number; y: number } | null = null;

    if (variant === "standCount") {
      const phoneRadius = interpolateRange(
        clampedMaxDistance,
        DISTANCE_LIMITS.min,
        DISTANCE_LIMITS.max,
        STAND_COUNT_PHONE_DISTANCE_RANGE.near,
        STAND_COUNT_PHONE_DISTANCE_RANGE.far
      );
      phoneCenter = {
        x: GROUND_ANCHOR.x - getAngleVector(angleState.centerAngle).x * phoneRadius,
        y: GROUND_ANCHOR.y - getAngleVector(angleState.centerAngle).y * phoneRadius,
      };

      const displayGroundIntersection = getGroundIntersection(phoneCenter, displayAngle, GROUND_ANCHOR.y);
      distanceLineEnd = displayGroundIntersection.point;
      validDistanceEnd = getPointAlongAngle(phoneCenter, displayAngle, displayGroundIntersection.distance * goodDistanceRatio);

      if (hasMinAngle) {
        minGuideEnd = getGroundIntersection(phoneCenter, minAngle, GROUND_ANCHOR.y).point;
      }
      if (hasMaxAngle) {
        maxGuideEnd = getGroundIntersection(phoneCenter, maxAngle, GROUND_ANCHOR.y).point;
      }
    } else {
      phoneCenter = {
        x: 300,
        y: interpolateRange(
          clampedMaxDistance,
          DISTANCE_LIMITS.min,
          DISTANCE_LIMITS.max,
          IMAGE_PHONE_Y_RANGE.near,
          IMAGE_PHONE_Y_RANGE.far
        ),
      };

      const distanceLineLength = interpolateRange(
        clampedMaxDistance,
        DISTANCE_LIMITS.min,
        DISTANCE_LIMITS.max,
        IMAGE_LINE_LENGTH_RANGE.near,
        IMAGE_LINE_LENGTH_RANGE.far
      );

      distanceLineEnd = getPointAlongAngle(phoneCenter, displayAngle, distanceLineLength);
      validDistanceEnd = getPointAlongAngle(phoneCenter, displayAngle, distanceLineLength * goodDistanceRatio);

      if (hasMinAngle) {
        minGuideEnd = getPointAlongAngle(phoneCenter, minAngle, distanceLineLength + GUIDE_PADDING);
      }
      if (hasMaxAngle) {
        maxGuideEnd = getPointAlongAngle(phoneCenter, maxAngle, distanceLineLength + GUIDE_PADDING);
      }
    }

    const minAngleLabelBase = getPointAlongAngle(phoneCenter, minAngle, IMAGE_LINE_LENGTH_RANGE.far + LABEL_PADDING);
    const maxAngleLabelBase = getPointAlongAngle(phoneCenter, maxAngle, IMAGE_LINE_LENGTH_RANGE.far + LABEL_PADDING);
    const longestBoxSideCm = clampValue(boxSizeCm ?? 50, BOX_SIZE_LIMITS.min, BOX_SIZE_LIMITS.max);
    const boxLineLength = interpolateRange(
      longestBoxSideCm,
      BOX_SIZE_LIMITS.min,
      BOX_SIZE_LIMITS.max,
      BOX_LINE_RANGE.min,
      BOX_LINE_RANGE.max
    );

    return {
      showBox,
      clampedMinDistance,
      clampedMaxDistance,
      phoneCenter,
      validDistanceEnd,
      distanceLineEnd,
      minGuideEnd,
      maxGuideEnd,
      minAngleLabel: {
        x: minAngleLabelBase.x - 18,
        y: minAngleLabelBase.y + 6,
      },
      maxAngleLabel: {
        x: maxAngleLabelBase.x + 18,
        y: maxAngleLabelBase.y + 6,
      },
      longestBoxSideCm,
      boxLineLength,
    };
  }, [
    angleState.centerAngle,
    boxSizeCm,
    displayAngle,
    hasMaxAngle,
    hasMinAngle,
    maxAngle,
    maxDistanceCm,
    minAngle,
    minDistanceCm,
    variant,
  ]);

  const ariaLabel =
    variant === "standCount"
      ? "Stand count capture visualization showing phone angle, distance range, and box size"
      : "Image measurement capture visualization showing phone angle and distance range";

  return (
    <div className={cn(className)}>
      <svg
        viewBox="0 0 470 276"
        className="h-72 w-full overflow-visible"
        role="img"
        aria-label={ariaLabel}>
        <defs>
          <linearGradient
            id="pitch-angle-ground"
            x1="0"
            y1="0"
            x2="1"
            y2="0">
            <stop
              offset="0%"
              stopColor="hsl(var(--muted))"
            />
            <stop
              offset="100%"
              stopColor="hsl(var(--accent))"
            />
          </linearGradient>
        </defs>

        <rect
          x={GROUND_LINE.x1 - 6}
          y={GROUND_LINE.y - 1}
          width={GROUND_LINE.x2 - GROUND_LINE.x1 + 12}
          height="28"
          rx="14"
          fill="url(#pitch-angle-ground)"
          opacity="0.75"
        />
        <line
          x1={GROUND_LINE.x1}
          y1={GROUND_LINE.y}
          x2={GROUND_LINE.x2}
          y2={GROUND_LINE.y}
          stroke="hsl(var(--border))"
          strokeWidth="2"
        />

        {preview.showBox ? (
          <line
            x1={GROUND_ANCHOR.x - preview.boxLineLength / 2}
            y1={GROUND_LINE.y}
            x2={GROUND_ANCHOR.x + preview.boxLineLength / 2}
            y2={GROUND_LINE.y}
            stroke={BOX_EDGE_COLOR}
            strokeWidth="4"
            strokeLinecap="round"
          />
        ) : null}

        {preview.minGuideEnd ? (
          <line
            x1={preview.phoneCenter.x}
            y1={preview.phoneCenter.y}
            x2={preview.minGuideEnd.x}
            y2={preview.minGuideEnd.y}
            stroke="hsl(var(--muted-foreground) / 0.6)"
            strokeWidth="2"
            strokeDasharray="5 5"
          />
        ) : null}
        {preview.maxGuideEnd ? (
          <line
            x1={preview.phoneCenter.x}
            y1={preview.phoneCenter.y}
            x2={preview.maxGuideEnd.x}
            y2={preview.maxGuideEnd.y}
            stroke="hsl(var(--muted-foreground) / 0.6)"
            strokeWidth="2"
            strokeDasharray="5 5"
          />
        ) : null}

        {hasMinAngle ? (
          <text
            x={preview.minAngleLabel.x}
            y={preview.minAngleLabel.y}
            textAnchor="end"
            className="fill-muted-foreground text-[10px] font-medium">
            Min {Math.round(minAngle)}°
          </text>
        ) : null}
        {hasMaxAngle ? (
          <text
            x={preview.maxAngleLabel.x}
            y={preview.maxAngleLabel.y}
            textAnchor="start"
            className="fill-muted-foreground text-[10px] font-medium">
            Max {Math.round(maxAngle)}°
          </text>
        ) : null}

        <line
          x1={preview.phoneCenter.x}
          y1={preview.phoneCenter.y}
          x2={preview.validDistanceEnd.x}
          y2={preview.validDistanceEnd.y}
          stroke="hsl(var(--primary))"
          strokeWidth="3"
          strokeLinecap="round"
          opacity="0.82"
        />
        <line
          x1={preview.validDistanceEnd.x}
          y1={preview.validDistanceEnd.y}
          x2={preview.distanceLineEnd.x}
          y2={preview.distanceLineEnd.y}
          stroke="hsl(var(--muted-foreground) / 0.75)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="5 5"
        />

        <g transform={`translate(${LEGEND_ORIGIN.x} ${LEGEND_ORIGIN.y})`}>
          <line
            x1="0"
            y1="0"
            x2="28"
            y2="0"
            stroke="hsl(var(--muted-foreground) / 0.75)"
            strokeWidth="2"
            strokeDasharray="5 5"
            strokeLinecap="round"
          />
          <text
            x="36"
            y="4"
            className="fill-muted-foreground text-[10px] font-medium">
            Too close under {Math.round(preview.clampedMinDistance)}cm
          </text>

          <line
            x1="0"
            y1="22"
            x2="28"
            y2="22"
            stroke="hsl(var(--primary))"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <text
            x="36"
            y="26"
            className="fill-muted-foreground text-[10px] font-medium">
            Good distance {Math.round(preview.clampedMinDistance)}-{Math.round(preview.clampedMaxDistance)}cm
          </text>

          {preview.showBox ? (
            <>
              <line
                x1="0"
                y1="44"
                x2="28"
                y2="44"
                stroke={BOX_EDGE_COLOR}
                strokeWidth="4"
                strokeLinecap="round"
              />
              <text
                x="36"
                y="48"
                className="fill-muted-foreground text-[10px] font-medium">
                Stand Count Box {Math.round(preview.longestBoxSideCm)}cm
              </text>
            </>
          ) : null}
        </g>

        <g transform={`translate(${preview.phoneCenter.x} ${preview.phoneCenter.y}) rotate(${-displayAngle})`}>
          <rect
            x="-30"
            y="-16"
            width="60"
            height="32"
            rx="11"
            fill="hsl(var(--background))"
            stroke="hsl(var(--foreground) / 0.2)"
            strokeWidth="2"
          />
          <rect
            x="-21"
            y="-9"
            width="42"
            height="18"
            rx="5"
            fill="hsl(var(--primary) / 0.14)"
          />
          <circle
            cx="-19"
            cy="0"
            r="2.5"
            fill="hsl(var(--foreground) / 0.4)"
          />
          <rect
            x="17"
            y="-5"
            width="3"
            height="10"
            rx="1.5"
            fill="hsl(var(--foreground) / 0.24)"
          />
        </g>

        <g transform={`translate(${PLANT_CENTER.x} ${PLANT_CENTER.y})`}>
          <path
            d="M 0 -24 C 4 -22 7 -15 7 -8 C 7 3 2 14 0 20 C -2 14 -7 3 -7 -8 C -7 -15 -4 -22 0 -24 Z"
            fill="hsl(var(--primary) / 0.92)"
          />
          <path
            d="M 0 -8 C 10 -17 18 -17 27 -8 C 17 -6 10 -2 4 6"
            fill="hsl(var(--primary) / 0.32)"
          />
          <path
            d="M 0 -4 C -10 -15 -18 -15 -27 -7 C -15 -3 -9 0 -4 6"
            fill="hsl(var(--primary) / 0.22)"
          />
          <rect
            x="-1.5"
            y="-4"
            width="3"
            height="24"
            rx="1.5"
            fill="hsl(var(--foreground) / 0.48)"
          />
        </g>

        <text
          x={GROUND_ANCHOR.x}
          y="248"
          textAnchor="middle"
          className="fill-muted-foreground text-[11px] font-medium">
          Plant on ground
        </text>
      </svg>
    </div>
  );
};

export default PitchAngleVisualization;
